/**
 * AST 기반 자동 생성된 검증 함수
 * 이 파일은 scripts/generate-validators.ts에 의해 자동 생성됩니다.
 * 수동으로 수정하지 마세요.
 * 
 * 생성 시간: 2026-10-02T04:29:21.355Z
 * 소스 파일: src/data/dto/login-response-dto
 */

import type { LoginResponseDto } from "@/data/dto/login-response-dto";

/**
 * LoginResponseDto 타입 가드 함수
 * AST 분석을 통해 자동 생성된 최적화된 검증 함수
 */
export function isLoginResponseDto(input: unknown): input is LoginResponseDto {
  if (typeof input !== "object" || input === null || Array.isArray(input)) {
    return false;
  }
  
  const obj = input as Record<string, unknown>;
  
  return (
    (obj.accessToken === undefined || typeof obj.accessToken === "string") &&
    (obj.refreshToken === undefined || typeof obj.refreshToken === "string") &&
    typeof obj.isExpiredSn === "boolean" &&
    (obj.id === undefined || typeof obj.id === "string") &&
    (obj.email === undefined || typeof obj.email === "string") &&
    (obj.name === undefined || typeof obj.name === "string") &&
    (obj.role === undefined || typeof obj.role === "string")
  );
}

/**
 * LoginResponseDto 상세 검증 결과 반환 함수
 * 검증 실패 시 상세한 에러 정보를 반환합니다.
 */
export interface IValidation<T> {
  success: boolean;
  data?: T;
  errors?: Array<{ path: string; expected: string; value: unknown }>;
}

export function validateLoginResponseDto(input: unknown): IValidation<LoginResponseDto> {
  const errors: Array<{ path: string; expected: string; value: unknown }> = [];
  
  if (typeof input !== "object" || input === null || Array.isArray(input)) {
    errors.push({
      path: "",
      expected: "object",
      value: input,
    });
    return {
      success: false,
      errors,
    };
  }
  
  const obj = input as Record<string, unknown>;
  
    if (typeof obj !== "object" || obj === null || Array.isArray(obj)) {
      errors.push({ path: "", expected: "object", value: obj });
    } else {
      const objObj = obj as Record<string, unknown>;
      // accessToken (optional)
      if (objObj.accessToken !== undefined) {
        if (typeof objObj.accessToken !== "string") {
          errors.push({ path: "accessToken", expected: "string", value: objObj.accessToken });
        }
      }
      // refreshToken (optional)
      if (objObj.refreshToken !== undefined) {
        if (typeof objObj.refreshToken !== "string") {
          errors.push({ path: "refreshToken", expected: "string", value: objObj.refreshToken });
        }
      }
      // isExpiredSn (required)
      if (objObj.isExpiredSn === undefined) {
        errors.push({ path: "isExpiredSn", expected: "boolean", value: undefined });
      } else {
        if (typeof objObj.isExpiredSn !== "boolean") {
          errors.push({ path: "isExpiredSn", expected: "boolean", value: objObj.isExpiredSn });
        }
      }
      // id (optional)
      if (objObj.id !== undefined) {
        if (typeof objObj.id !== "string") {
          errors.push({ path: "id", expected: "string", value: objObj.id });
        }
      }
      // email (optional)
      if (objObj.email !== undefined) {
        if (typeof objObj.email !== "string") {
          errors.push({ path: "email", expected: "string", value: objObj.email });
        }
      }
      // name (optional)
      if (objObj.name !== undefined) {
        if (typeof objObj.name !== "string") {
          errors.push({ path: "name", expected: "string", value: objObj.name });
        }
      }
      // role (optional)
      if (objObj.role !== undefined) {
        if (typeof objObj.role !== "string") {
          errors.push({ path: "role", expected: "string", value: objObj.role });
        }
      }
    }
  
  if (errors.length === 0 && isLoginResponseDto(input)) {
    return {
      success: true,
      data: input,
    };
  }
  
  return {
    success: false,
    errors: errors.length > 0 ? errors : [
      {
        path: "",
        expected: "LoginResponseDto",
        value: input,
      },
    ],
  };
}

/**
 * LoginResponseDto 검증 후 에러 throw 함수
 * 검증 실패 시 상세한 에러 정보를 포함하여 에러를 throw합니다.
 */
export function assertLoginResponseDto(input: unknown): LoginResponseDto {
  const validation = validateLoginResponseDto(input);
  if (!validation.success) {
    const errorDetails = validation.errors?.map(err => 
      `  - ${err.path}: 예상 타입 ${err.expected}, 실제 값: ${JSON.stringify(err.value)}`
    ).join("\n") || "알 수 없는 오류";
    throw new Error(`[assertLoginResponseDto] :: 검증 실패: LoginResponseDto 타입과 일치하지 않습니다\n\n오류 상세:\n${errorDetails}`);
  }
  return validation.data!;
}
